import React from 'react';
import { IMPORT_FORMAT_EXAMPLES, type ImportFormatExampleKey } from '../../data/importFormatExamples';
import { IMPORT_PRIMARY_EXAMPLE } from './importConstants';

interface ImportInputStepProps {
  inputText: string;
  onInputTextChange: (value: string) => void;
  dragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (files: FileList | null) => void;
  acceptAttr: string;
  isExtractingFile: boolean;
  uploadedFileName: string | null;
  uploadedFileSize: number | null;
  excelExtractInfo: { rowCount: number; format: string; sheetName: string } | null;
  onClearFile: () => void;
  preferDeeperCleanup: boolean;
  onPreferDeeperCleanupChange: (value: boolean) => void;
  showAdvancedOptions: boolean;
  onToggleAdvanced: () => void;
  onLoadExample: (key: ImportFormatExampleKey) => void;
  onTryPrimaryExample: () => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Step 1 — Add your build list (paste + upload). No technical mode choices.
 */
export const ImportInputStep: React.FC<ImportInputStepProps> = ({
  inputText,
  onInputTextChange,
  dragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileClick,
  fileInputRef,
  onFileChange,
  acceptAttr,
  isExtractingFile,
  uploadedFileName,
  uploadedFileSize,
  excelExtractInfo,
  onClearFile,
  preferDeeperCleanup,
  onPreferDeeperCleanupChange,
  showAdvancedOptions,
  onToggleAdvanced,
  onLoadExample,
  onTryPrimaryExample,
}) => {
  const lineCount = inputText.split('\n').filter((l) => l.trim()).length;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-white mb-1.5">Import your build list</h3>
        <p className="text-sm text-gray-400 leading-relaxed">
          Paste the list you already keep in Notes, or upload a spreadsheet or document. Bilt will
          organize the parts, sections, brands, and specifications for you to review.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Paste */}
        <div className="space-y-2">
          <label htmlFor="import-paste-area" className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Paste a list
          </label>
          <textarea
            id="import-paste-area"
            value={inputText}
            onChange={(e) => onInputTextChange(e.target.value)}
            placeholder={`Engine\n- Manley 9:1 Pistons\n- Bosch 84mm Throttle Body\n\nWheels and Tires\n- …`}
            className="w-full min-h-[180px] sm:min-h-[220px] bg-[#0a0a0a]/80 border border-white/[0.08] rounded-xl px-3.5 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500/50 resize-y"
            spellCheck={false}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
            <span>{lineCount > 0 ? `${lineCount} line${lineCount === 1 ? '' : 's'}` : 'Ready for your list'}</span>
            {inputText.trim() && (
              <button
                type="button"
                onClick={() => onInputTextChange('')}
                className="text-gray-400 hover:text-white min-h-[36px]"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Upload */}
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Upload a file</p>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttr}
            className="sr-only"
            onChange={(e) => onFileChange(e.target.files)}
            aria-label="Choose a build list file"
          />
          {uploadedFileName ? (
            <div className="rounded-xl border border-orange-500/30 bg-orange-950/20 p-4 space-y-3 min-h-[180px] sm:min-h-[220px] flex flex-col justify-center">
              <p className="text-sm text-white font-medium break-all">{uploadedFileName}</p>
              <p className="text-xs text-gray-400">
                {uploadedFileSize != null ? formatBytes(uploadedFileSize) : ''}
                {excelExtractInfo
                  ? ` · ${excelExtractInfo.rowCount} ${
                      excelExtractInfo.format === 'build_list' ? 'parts' : 'rows'
                    }${excelExtractInfo.sheetName ? ` · ${excelExtractInfo.sheetName}` : ''}`
                  : ''}
              </p>
              {excelExtractInfo?.format === 'structured_json' && (
                <p className="text-xs text-orange-200/90">
                  We found columns for category, brand, part, and related details.
                </p>
              )}
              {excelExtractInfo?.format === 'build_list' && (
                <p className="text-xs text-orange-200/90">
                  We found a sectioned build list with parts and prices. Totals were skipped.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={onFileClick} className="text-xs px-3 py-2 rounded-lg border border-white/[0.10] text-gray-300 hover:text-white min-h-[40px]">
                  Replace
                </button>
                <button type="button" onClick={onClearFile} className="text-xs px-3 py-2 rounded-lg border border-white/[0.10] text-gray-300 hover:text-white min-h-[40px]">
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onFileClick}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              disabled={isExtractingFile}
              className={`w-full min-h-[180px] sm:min-h-[220px] rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
                dragOver
                  ? 'border-orange-500 bg-orange-950/30'
                  : 'border-white/[0.08] bg-[#0c0c0c]/50 hover:border-white/[0.18]'
              }`}
            >
              {isExtractingFile ? (
                <span className="text-sm text-gray-300">Reading file…</span>
              ) : (
                <>
                  <span className="block text-sm font-medium text-white mb-1">Drop a file or browse</span>
                  <span className="block text-xs text-gray-500">Excel · CSV · PDF · Text · max 10 MB</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onTryPrimaryExample}
          className="text-xs px-3 py-2 rounded-full border border-white/[0.10] text-gray-300 hover:text-white hover:border-orange-500/50 min-h-[40px]"
        >
          Try an example
        </button>
        <button
          type="button"
          onClick={onToggleAdvanced}
          className="text-xs text-gray-500 hover:text-gray-300 min-h-[40px]"
          aria-expanded={showAdvancedOptions}
        >
          {showAdvancedOptions ? 'Hide advanced options' : 'Advanced options'}
        </button>
      </div>

      {showAdvancedOptions && (
        <div className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preferDeeperCleanup}
              onChange={(e) => onPreferDeeperCleanupChange(e.target.checked)}
              className="mt-1 rounded border-white/[0.14] text-orange-500 focus:ring-orange-500"
            />
            <span>
              <span className="text-sm text-white block">Prefer deeper cleanup for very messy notes</span>
              <span className="text-xs text-gray-500">
                Most people can leave this off. Bilt chooses how to organize automatically.
              </span>
            </span>
          </label>
          <div className="flex flex-wrap gap-2 pt-1">
            {(
              [
                ['simpleList', 'Simple list'],
                ['bulletList', 'Bullets'],
                ['csv', 'CSV'],
                ['receipt', 'Receipt'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => onLoadExample(key)}
                className="text-xs px-2.5 py-1.5 rounded-full border border-white/[0.08] text-gray-400 hover:text-white min-h-[36px]"
              >
                {label}
              </button>
            ))}
          </div>
          <pre className="text-[10px] text-gray-600 whitespace-pre-wrap font-mono leading-relaxed border-t border-white/[0.06] pt-3">
            {IMPORT_PRIMARY_EXAMPLE}
          </pre>
          <p className="sr-only">{IMPORT_FORMAT_EXAMPLES.simpleList}</p>
        </div>
      )}
    </div>
  );
};

export default ImportInputStep;
